import { useCallback, useEffect, useState } from "react";
import { Button, Card, EmptyState, Input, NoticeBox, ProgressBar, Tags } from "../../../../ui";
import { useAccess } from "../../../../../access";
import {
  decideAdminStoreOnboardingAPI,
  getAdminStoreOnboardingAPI,
  getDraftDocumentUrlAPI,
} from "../../../../../utils/api/storeOnboardingDraft";

/*
  Administrator review of the STORE's onboarding information (Phase 3): status, completion, what is missing, the uploaded documents
  and approve / reject. Approving the information is NOT a subscription decision: the text says so and the server never activates one.
*/

const TEXT = {
  ar: {
    title: "بيانات المتجر (مصدر الحقيقة)",
    loading: "جاري تحميل بيانات المتجر...",
    error: "تعذر تحميل بيانات المتجر.",
    retry: "إعادة المحاولة",
    status: { new: "جديد", inProgress: "قيد الإكمال", pendingReview: "بانتظار المراجعة", approved: "تمت الموافقة", rejected: "مرفوض للتعديل" },
    completion: "اكتمال البيانات",
    missing: "بيانات ناقصة",
    none: "لا توجد بيانات ناقصة.",
    documents: "المستندات",
    noFile: "لم يُرفع",
    view: "عرض",
    approve: "الموافقة على البيانات",
    reject: "إرجاع للتعديل",
    reason: "سبب الإرجاع",
    confirmReject: "تأكيد الإرجاع",
    cancel: "إلغاء",
    note: "الموافقة على البيانات لا تُفعّل الاشتراك ولا تعني الدفع.",
    only: "تُراجع البيانات فقط عند حالة «بانتظار المراجعة».",
    history: "السجل",
    done: "تم تسجيل القرار.",
  },
  en: {
    title: "Store information (source of truth)",
    loading: "Loading the store...",
    error: "Could not load the store.",
    retry: "Try again",
    status: { new: "New", inProgress: "In progress", pendingReview: "Pending review", approved: "Approved", rejected: "Returned for changes" },
    completion: "Completion",
    missing: "Missing information",
    none: "Nothing is missing.",
    documents: "Documents",
    noFile: "Not uploaded",
    view: "View",
    approve: "Approve the information",
    reject: "Return for changes",
    reason: "Reason",
    confirmReject: "Confirm",
    cancel: "Cancel",
    note: "Approving the information does not activate a subscription and is not a payment.",
    only: "Information can be reviewed only while it is “Pending review”.",
    history: "History",
    done: "Decision recorded.",
  },
};
const VARIANT = { new: "neutral", inProgress: "info", pendingReview: "warning", approved: "success", rejected: "danger" };

export default function StoreReview({ storeCode, lang = "ar", onDecided }) {
  const t = TEXT[lang === "en" ? "en" : "ar"];
  const { can } = useAccess();
  const canReview = can("storeOnboardingAdmin.review");
  const [state, setState] = useState("loading");
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("auth-token");

  const load = useCallback(async () => {
    setState("loading");
    try {
      setResult(await getAdminStoreOnboardingAPI(token(), storeCode));
      setState("ready");
    } catch (error) {
      setMessage({ kind: "error", text: error.message });
      setState("error");
    }
  }, [storeCode]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (action) => {
    setBusy(true);
    setMessage(null);
    try {
      await decideAdminStoreOnboardingAPI(token(), storeCode, action, reason);
      setRejecting(false);
      setReason("");
      setMessage({ kind: "success", text: t.done });
      await load();
      onDecided?.();
    } catch (error) {
      setMessage({ kind: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const open = async (key) => {
    try {
      window.open(await getDraftDocumentUrlAPI(token(), storeCode, key), "_blank", "noopener,noreferrer");
    } catch (error) {
      setMessage({ kind: "error", text: error.message });
    }
  };

  if (state === "loading" && !result) return <EmptyState variant="loading" compact title={t.loading} />;
  if (state === "error" && !result) {
    return (
      <EmptyState variant="error" compact title={t.error} action={<Button size="sm" onClick={load}>{t.retry}</Button>}>
        {message?.text}
      </EmptyState>
    );
  }
  const { data } = result;
  const { onboarding, completion, documents } = data;
  const pending = onboarding.status === "pendingReview";

  return (
    <Card className="store-review" data-testid="store-review">
      <h3>{t.title} · <span dir="ltr">{storeCode}</span></h3>
      {message && (
        <NoticeBox variant={message.kind === "success" ? "success" : "danger"}>
          <span role={message.kind === "error" ? "alert" : "status"}>{message.text}</span>
        </NoticeBox>
      )}
      <p>
        <Tags variant={VARIANT[onboarding.status] || "neutral"} appearance="soft">{t.status[onboarding.status] || onboarding.status}</Tags>{" "}
        <strong>{t.completion}: {completion.percent}%</strong>
      </p>
      <ProgressBar value={completion.percent} label={t.completion} />
      {onboarding.rejectionReason && <p dir="auto">{t.reason}: {onboarding.rejectionReason}</p>}

      <h4>{t.missing}</h4>
      {completion.missing.length === 0 ? (
        <p>{t.none}</p>
      ) : (
        <ul>
          {completion.missing.map((item) => (
            <li key={item.id}>{lang === "en" ? `${item.sectionEn}: ${item.en}` : `${item.sectionAr}: ${item.ar}`}</li>
          ))}
        </ul>
      )}

      <h4>{t.documents}</h4>
      <ul>
        {documents.map((doc) => (
          <li key={doc.key}>
            {lang === "en" ? doc.titleEn : doc.titleAr}: {doc.file ? <><span dir="auto">{doc.file.name}</span> <Button size="sm" appearance="outline" onClick={() => open(doc.key)}>{t.view}</Button></> : t.noFile}
          </li>
        ))}
      </ul>

      {canReview && (
        <div className="store-review-actions">
          {pending ? (
            rejecting ? (
              <>
                <Input label={t.reason} value={reason} onChange={(event) => setReason(event.target.value)} />
                <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => decide("reject")}>{t.confirmReject}</Button>
                <Button appearance="outline" disabled={busy} onClick={() => setRejecting(false)}>{t.cancel}</Button>
              </>
            ) : (
              <>
                <Button disabled={busy} onClick={() => decide("approve")}>{t.approve}</Button>
                <Button variant="danger" appearance="outline" disabled={busy} onClick={() => setRejecting(true)}>{t.reject}</Button>
              </>
            )
          ) : (
            <p>{t.only}</p>
          )}
          <p>{t.note}</p>
        </div>
      )}
      {!!result.history?.length && (
        <details>
          <summary>{t.history}</summary>
          <ul>
            {result.history.map((entry, index) => (
              <li key={index}>{entry.action}: {entry.from || "-"} → {entry.to}{entry.reason ? ` (${entry.reason})` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}
